import { ChangeDetectorRef, Component, forwardRef, Input, OnInit, input, output, viewChild } from "@angular/core";
import { NgModel, NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';
import { Observable, takeUntil } from "rxjs";
import { Lot } from '../../../types/lot';
import { PCPLotSelectorBaseComponent } from '../pcp-lot-selector.basecomponent';

import { ErrormessageComponent } from "../../validation/errormessage/errormessage.component";
import { AutoCompleteModule } from "primeng/autocomplete";
import { FluidModule } from "primeng/fluid";

@Component({
	selector: "pcp-lot-autocomplete",
	templateUrl: "./pcp-lot-autocomplete.component.html",
	styleUrls: [],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			multi: true,
			useExisting: forwardRef(() => PCPLotAutoCompleteComponent),
		}
	],
	standalone: true,
	imports: [AutoCompleteModule, FormsModule, FluidModule, ErrormessageComponent]
})
export class PCPLotAutoCompleteComponent extends PCPLotSelectorBaseComponent {
	readonly lotSelection = viewChild<NgModel>("lotSelection");

	// TODO: Skipped for migration because:
	//  Accessor inputs cannot be migrated as they are too complex.
	@Input() set lots(lots: Lot[]) {
		if (!lots || lots.length === 0) {
			this._lots = [];
			this.lotSuggestions = [];
		}
		else {
			this._lots = lots;
			this.lotSuggestions = this.lotsToAutoCompleteSuggestions(lots);
		}
	}

	get lots(): Lot[] {
		return this._lots;
	}

	_lots: Lot[] = [];

	_observable: Observable<Lot[]>;

	// TODO: Skipped for migration because:
	//  Accessor inputs cannot be migrated as they are too complex.
	@Input() set lotsObservable(observable: Observable<Lot[]>) {
		this._observable = observable;
		this.reloadOptions();
	};

	get lotsObservable(): Observable<Lot[]> {
		return this._observable;
	}

	_selectedLot: Lot;

	readonly placeholder = input<string>("Lots");
	readonly forceSelection = input<boolean>(true);

	get label(): string {
		if (this.isLoading) return this.loadingText;

		return this.placeholder();
	}

	// TODO: Skipped for migration because:
	//  Accessor inputs cannot be migrated as they are too complex.
	@Input() set selectedLot(lot: Lot) {
		if (lot) {
			lot.displayText = this.lotToDisplayText(lot);
		}
		this._selectedLot = lot;
		this.propagateChange(this._selectedLot);
	};

	get selectedLot(): Lot {
		return this._selectedLot;
	}

	readonly selectedLotChange = output<Lot>();

	readonly lotsLoaded = output<any>();

	readonly onCompleteFilter = input<(filterString: string, lotsToFilter: Lot[]) => void>(this.filter);

	// TODO: Skipped for migration because:
	//  Your application code writes to the input. This prevents migration.
	@Input() lotSuggestions: Lot[] = [];

	constructor(private readonly cdr: ChangeDetectorRef) {
		super();
	}

	writeValue(obj: any) {
		this.selectedLot = obj;
	}

	onChange() {
		this.selectedLotChange.emit(this._selectedLot);
	}

	onComplete(event: any) {
		const filterString: string = event?.query ?? "";
		const fn = this.onCompleteFilter() ?? this.filter;
		fn.call(this, filterString, this._lots);
		// Force update so p-autoComplete suggestions setter runs while loading is still true
		this.cdr.detectChanges();
	}

	/** Populate suggestions when dropdown button is clicked so panel can open. */
	onDropdownClick(): void {
		this.lotSuggestions = this.lotsToAutoCompleteSuggestions(this._lots);
		this.cdr.detectChanges();
	}

	filter(filterString: string, lots: Lot[]) {
		const theFilterWasCleared: boolean = filterString === "";
		if (theFilterWasCleared) {
			this.lotSuggestions = this.lotsToAutoCompleteSuggestions(this._lots);
			return;
		}

		const filteredCommunities = lots.filter((lotOption: Lot) => {
			return lotOption.displayText.toLowerCase().indexOf(filterString.toLowerCase()) !== -1;
		});

		this.lotSuggestions = filteredCommunities;
	}

	clearLot() {
		this._selectedLot = null;
	}

	lotsToAutoCompleteSuggestions(lots: Lot[]) {
		const byDisplayText = (a, b) => a.displayText > b.displayText ? 1 : -1;
		return lots.map((lot: Lot) => {
			lot.displayText = this.lotToDisplayText(lot);
			return lot;
		}).sort(byDisplayText);
	}

	lotToDisplayText(lot: Lot): string {
		if (!lot) { return; }

		const lotBlock = (lot.lotBlock || "").trim();
		const street = (lot.streetAddress1 || "").trim();
		if (this.isMultiCommunityLotList()) {
			const key = (lot.community?.sourceKey ?? "").trim();
			return `(${key}) ${lotBlock} - ${street}`.trim();
		}
		return `${lotBlock} - ${street}`.trim();
	}

	reloadOptions(): void {
		if (!this.lotsObservable) { return; }

		this.isLoading = true;
		this.lotsObservable
			.pipe(takeUntil(this.ngUnsubscribe$))
			.subscribe((communities) => {
				const communitiesWithDisplayText = communities.map((community) => {
					community.displayText = this.lotToDisplayText(community);
					return community;
				});

				this.lots = communitiesWithDisplayText;
				this.isLoading = false;
				// TODO: The 'emit' function requires a mandatory any argument
				this.lotsLoaded.emit(null);
			});
	}

	clearOptions() {
		this.lots = [];
	}

	hasLots(): boolean {
		return this.lots && this.lots.length > 0;
	}
}
